import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebase/config';
import { collection, getDocs, updateDoc, addDoc, doc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useAuthContext } from '../hooks/useAuthContext';
import './AboutUsForm.css'

const AboutUsForm = () => {
  const { user } = useAuthContext();
  const [formData, setFormData] = useState({
    title: '',
    description1: '',
    description2: '',
    image1: null,
    image2: null
  });
  const [progress, setProgress] = useState({ image1: 0, image2: 0 });
  const [loading, setLoading] = useState(false);
  const [existingDocument, setExistingDocument] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user) {
        const idTokenResult = await user.getIdTokenResult();
        setIsAdmin(idTokenResult.claims?.admin || false);
      }
    };
    checkAdminStatus();
  }, [user]);

  // Load existing aboutUs document
  useEffect(() => {
    const loadExistingDocument = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'aboutUs'));
        if (!querySnapshot.empty) {
          const doc = querySnapshot.docs[0];
          const data = doc.data();
          setExistingDocument(doc);
          setFormData({
            title: data.title || '',
            description1: data.description1 || '',
            description2: data.description2 || '',
            image1: null,
            image2: null
          });
        }
      } catch (error) {
        console.error('Error loading existing document:', error);
      }
    };
    
    if (isAdmin) {
      loadExistingDocument();
    }
  }, [isAdmin]);

  const handleImageChange = (e, imageNum) => {
    if (e.target.files[0]) {
      setFormData({
        ...formData,
        [imageNum]: e.target.files[0]
      });
    }
  };

  const uploadImage = async (image, imageName) => {
    return new Promise((resolve, reject) => {
      const storageRef = ref(storage, `aboutUs/${Date.now()}_${image.name}`);
      const uploadTask = uploadBytesResumable(storageRef, image);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progressPercentage = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setProgress(prev => ({
            ...prev,
            [imageName]: progressPercentage
          }));
        },
        (error) => {
          console.error(`${imageName} upload failed:`, error);
          reject(error);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadURL);
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!formData.title || !formData.description1 || !formData.description2) {
      alert('Please fill in all text fields');
      setLoading(false);
      return;
    }

    try {
      // Upload new images if selected
      let image1Url = existingDocument?.data()?.image1Url || null;
      let image2Url = existingDocument?.data()?.image2Url || null;

      if (formData.image1) {
        image1Url = await uploadImage(formData.image1, 'image1');
      }

      if (formData.image2) {
        image2Url = await uploadImage(formData.image2, 'image2');
      }

      // Prepare document data
      const docData = {
        title: formData.title,
        description1: formData.description1,
        description2: formData.description2,
        image1Url,
        image2Url,
        updatedAt: new Date(),
        updatedBy: user.uid
      };

      if (existingDocument) {
        // Update existing document
        await updateDoc(doc(db, 'aboutUs', existingDocument.id), docData);
        alert('About Us page updated successfully');
      } else {
        // Create new document if none exists
        await addDoc(collection(db, 'aboutUs'), {
          ...docData,
          createdAt: new Date(),
          createdBy: user.uid
        });
        alert('About Us page created successfully');
      }

      // Reset form images only (keep text data for editing)
      setFormData(prev => ({
        ...prev,
        image1: null,
        image2: null
      }));
      setProgress({ image1: 0, image2: 0 });

    } catch (error) {
      console.error('Submit Error:', error);
      alert('Error updating About Us page: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Don't render form if user is not admin
  if (!isAdmin) {
    return <div></div>;
  }

  return (
    <div className="form-container">
      <h2>{existingDocument ? 'Update About Us Page' : 'Create About Us Page'}</h2>
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Title</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({...formData, title: e.target.value})}
            placeholder="Enter title"
            required
          />
        </div>

        <div className="form-group">
          <label>First Description</label>
          <textarea
            value={formData.description1}
            onChange={(e) => setFormData({...formData, description1: e.target.value})}
            placeholder="Enter first description"
            required
          />
        </div>

        <div className="form-group">
          <label>First Image</label>
          <input
            type="file"
            onChange={(e) => handleImageChange(e, 'image1')}
            accept="image/*"
          />
          {progress.image1 > 0 && <p>Image 1 upload progress: {progress.image1.toFixed(2)}%</p>}
          {existingDocument?.data()?.image1Url && !formData.image1 && (
            <p className="existing-image-note">Current image will be kept if no new image is selected</p>
          )}
        </div>

        <div className="form-group">
          <label>Second Description</label>
          <textarea
            value={formData.description2}
            onChange={(e) => setFormData({...formData, description2: e.target.value})}
            placeholder="Enter second description"
            required
          />
        </div>

        <div className="form-group">
          <label>Second Image</label>
          <input
            type="file"
            onChange={(e) => handleImageChange(e, 'image2')}
            accept="image/*"
          />
          {progress.image2 > 0 && <p>Image 2 upload progress: {progress.image2.toFixed(2)}%</p>}
          {existingDocument?.data()?.image2Url && !formData.image2 && (
            <p className="existing-image-note">Current image will be kept if no new image is selected</p>
          )}
        </div>
        
        <div className="form-group">
          <button type="submit" disabled={loading}>
            {loading ? 'Saving...' : existingDocument ? 'Update About Us' : 'Create About Us'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AboutUsForm;